/*
 * Bordado na página de produto — ADR-0017.
 *
 * O tema desenha a UI; o Live Product Options continua dono do vocabulário. Os
 * nomes de property abaixo não são copy: mudar qualquer um faz o pedido sair
 * com dois vocabulários e a expedição ler errado.
 *
 * O componente só existe quando o produto tem calibragem publicada. Sem ela, o
 * bloco não renderiza e o app block do LPO segue no lugar.
 *
 * A cobrança é do tema, não do LPO: o bordado entra no carrinho como uma linha
 * própria do produto "Bordado" (o mesmo da grade de kit, ADR-0012), ao lado da
 * peça, que leva só as properties de personalização. `_cl_options` nunca vai no
 * payload — com ele o LPO cobraria uma segunda vez.
 */

(function (window, customElements, HTMLElement) {
  'use strict';

  const PROPRIEDADE_LADO = 'Lado do bordado';

  /** Lado do ícone, gravado só quando a peça leva texto E ícone (ENN-92): o oposto ao do nome. */
  const PROPRIEDADE_LADO_ICONE = 'Lado do ícone';

  /** Property que leva a arte do logo, no vocabulário do Live Product Options (a mesma da grade). */
  const PROPRIEDADE_ARQUIVO_LOGO = 'Upload logo';
  const PROPRIEDADE_LOGO = 'Bordado logo';

  /** Property da peça com o NOME do ícone escolhido (ADR-0027): é o que a produção lê. */
  const PROPRIEDADE_ICONE = 'Bordado ícone';

  /** Mesmo limite da grade de kit (`LIMITE_LOGO_MB` em `kit-builder.js`); mudar um muda o outro. */
  const LIMITE_LOGO_MB = 20;

  /**
   * Monta as line item properties do bordado.
   *
   * Campo em branco não vira property: property vazia só polui a ordem de
   * serviço. O lado só é gravado quando o produto oferece escolha — ausência
   * significa o padrão de hoje.
   *
   * Com ícone sozinho (sem texto) `campos` vêm vazios e `cor`/`fonte` em branco:
   * nada disso vira property — cor de linha e fonte valem só para o texto.
   *
   * @param {{campos: Record<string,string>, cor: string, fonte: string, logo: boolean, lado: string|null, icone?: string|null}} dados
   * @param {{campos: {nome: string}[], fonte: {nome: string}|null}} contrato
   * @returns {Record<string, string>}
   */
  function montar(dados, contrato) {
    const props = { 'Adicione bordado': 'Sim' };

    contrato.campos.forEach(({ nome }) => {
      const valor = (dados.campos?.[nome] ?? '').trim();
      if (valor) props[nome] = valor;
    });

    if (dados.cor) props.Cores = dados.cor;
    if (contrato.fonte && dados.fonte) props[contrato.fonte.nome] = dados.fonte;
    if (dados.logo) props[PROPRIEDADE_LOGO] = 'Add';
    if (dados.icone) props[PROPRIEDADE_ICONE] = dados.icone;
    if (dados.lado) props[PROPRIEDADE_LADO] = window.FoxTheme.bordadoGeometria.rotuloDoLado(dados.lado);

    const temTexto = contrato.campos.some(({ nome }) => (dados.campos?.[nome] ?? '').trim());
    const opostoDoIcone = window.FoxTheme.bordadoGeometria.ladoOposto(dados.lado);
    if (dados.icone && temTexto && opostoDoIcone) {
      props[PROPRIEDADE_LADO_ICONE] = window.FoxTheme.bordadoGeometria.rotuloDoLado(opostoDoIcone);
    }

    return props;
  }

  /**
   * Monta as linhas que a PDP envia ao carrinho: a peça, com a personalização,
   * a cobrança do bordado de texto e, com logo ou ícone, a cobrança de cada um —
   * todas ligadas à peça pela mesma referência. Ordem: peça, texto, logo, ícone.
   *
   * `cobranca` é a do bordado de TEXTO e é `null` quando a peça leva só ícone e/ou
   * logo (ADR-0027, D30): sem texto não entra a linha `Bordado` de R$ 40,00. Logo
   * sozinho cobra só o `Bordado de logo` (R$ 90,00).
   *
   * `entradas` são os pares `[nome, valor]` do formulário de compra. Os
   * `properties[X]` do form entram na peça, menos `_cl_options` (cobrança dupla)
   * e menos o que a personalização já define — a personalização vence.
   *
   * A cobrança do logo é marcada com `_bordado_tipo: 'logo'`, igual à grade: a
   * guarda do carrinho precisa dela para saber qual das duas cobranças sumiu.
   * Peça com logo e sem `cobrancaLogo` é erro, nunca envio: o logo sairia de graça.
   * O ícone segue a mesma regra, com `_bordado_tipo: 'icone'`.
   *
   * @param {{entradas: Iterable<[string, unknown]>, personalizacao: Record<string,string>, cobranca: {varianteId: number}|null, cobrancaLogo?: {varianteId: number}|null, cobrancaIcone?: {varianteId: number}|null, referencia: string}} args
   * @returns {object[]}
   */
  function montarItens({ entradas, personalizacao, cobranca, cobrancaLogo = null, cobrancaIcone = null, referencia }) {
    const campos = {};
    const propriedades = {};

    if (personalizacao[PROPRIEDADE_LOGO] && !cobrancaLogo?.varianteId) {
      throw new Error('logo marcado sem produto de cobrança do logo');
    }
    if (personalizacao[PROPRIEDADE_ICONE] && !cobrancaIcone?.varianteId) {
      throw new Error('ícone marcado sem produto de cobrança do ícone');
    }
    // Sem texto a peça precisa de ícone e/ou logo (D30): senão não há o que cobrar.
    if (!cobranca?.varianteId && !personalizacao[PROPRIEDADE_ICONE] && !personalizacao[PROPRIEDADE_LOGO]) {
      throw new Error('bordado sem cobrança');
    }

    for (const [nome, valor] of entradas) {
      if (typeof valor !== 'string') continue;

      const propriedade = /^properties\[(.*)\]$/s.exec(nome);
      if (propriedade) {
        const chave = propriedade[1];
        if (chave !== '_cl_options' && !(chave in personalizacao)) propriedades[chave] = valor;
      } else {
        campos[nome] = valor;
      }
    }

    const quantidade = Math.max(1, Math.trunc(Number(campos.quantity)) || 1);
    const peca = { id: Number(campos.id), quantity: quantidade };
    if (campos.selling_plan) peca.selling_plan = campos.selling_plan;
    peca.properties = { ...propriedades, ...personalizacao, _bordado_ref: referencia };

    const itens = [peca];

    if (cobranca?.varianteId) {
      itens.push({ id: cobranca.varianteId, quantity: quantidade, properties: { _bordado_de: referencia } });
    }

    if (personalizacao[PROPRIEDADE_LOGO]) {
      itens.push({
        id: cobrancaLogo.varianteId,
        quantity: quantidade,
        properties: { _bordado_de: referencia, _bordado_tipo: 'logo' },
      });
    }

    if (personalizacao[PROPRIEDADE_ICONE]) {
      itens.push({
        id: cobrancaIcone.varianteId,
        quantity: quantidade,
        properties: { _bordado_de: referencia, _bordado_tipo: 'icone' },
      });
    }

    return itens;
  }

  /**
   * Corpo multipart da peça com a arte do logo. Arquivo em line item property
   * exige multipart, e multipart carrega um item por chamada — por isso a peça
   * vai sozinha e as cobranças, em JSON, na chamada seguinte (como na grade).
   *
   * @param {{id: number, quantity: number, selling_plan?: string, properties: Record<string,string>}} peca
   * @param {Blob} arquivo
   * @returns {FormData}
   */
  function montarCorpoComArte(peca, arquivo) {
    const corpo = new FormData();

    corpo.append('id', peca.id);
    corpo.append('quantity', peca.quantity);
    if (peca.selling_plan) corpo.append('selling_plan', peca.selling_plan);
    Object.entries(peca.properties).forEach(([nome, valor]) => corpo.append(`properties[${nome}]`, valor));
    corpo.append(`properties[${PROPRIEDADE_ARQUIVO_LOGO}]`, arquivo);

    return corpo;
  }

  /**
   * O que impede o logo de ser confirmado, ou `null`. Arte obrigatória e com
   * teto de tamanho, igual à grade.
   *
   * @param {{logo: boolean, arquivo: {size: number}|null}} dados
   * @returns {'faltando'|'grande'|null}
   */
  function impedimentoDoLogo({ logo, arquivo }) {
    if (!logo) return null;
    if (!arquivo) return 'faltando';

    return arquivo.size > LIMITE_LOGO_MB * 1024 * 1024 ? 'grande' : null;
  }

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.bordadoPropriedades = { montar, montarItens, montarCorpoComArte, impedimentoDoLogo, LIMITE_LOGO_MB };

  if (customElements.get('bordado-pdp')) return;

  const LARGURA_CANVAS = 1200;

  /**
   * Linha "Personalize" da PDP e ponte entre a página e o modal de bordado
   * (`<bordado-modal>`, ENN-90).
   *
   * O que é da PDP e fica aqui: decidir se o bordado é oferecido, a linha da
   * página, a fonte de tamanho (o seletor de variante da página), a fonte de cor
   * e o envio ao carrinho (ADR-0023). O menu, os subpainéis, o resumo e o preview
   * são do modal, que a grade de kit reaproveita com outras fontes e outro rodapé.
   */
  class BordadoPdp extends HTMLElement {
    #dados;
    #cobrancas;
    #contrato;
    #modal = null;
    #botao = null;
    #linha = null;
    #personalizacao = null;
    #arquivoLogo = null;
    #cobrarTexto = true;
    #aoEnviar = null;
    #pronto = false;

    connectedCallback() {
      this.#dados = JSON.parse(this.querySelector('[data-bordado-dados]').textContent);
      this.#modal = this.querySelector('bordado-modal');
      this.#cobrancas = JSON.parse(this.querySelector('[data-bordado-modal-dados]').textContent).cobrancas;

      // Nunca oferecer personalização sem cobrança: o bordado sairia de graça.
      if (!this.#cobrancas.texto?.varianteId) {
        console.warn('[bordado] produto de cobrança "bordado" indisponível; bordado não oferecido');
        this.hidden = true;
        return;
      }

      this.#contrato = window.FoxTheme.bordadoContrato.lerContrato(window.Cloudlift);

      // O Live Product Options publica quais produtos aceitam bordado — parte
      // das toucas (tweed, Star 2.0, Glam, Disco, turbantes, gorro rosa) não
      // aceita porque a agulha não perfura tecido brilhante. Sem essa checagem,
      // calibrar uma delas ofereceria bordado numa peça que não pode ser bordada.
      if (!this.#contrato || !this.#contrato.handles.has(this.#dados.handle)) {
        console.warn('[bordado] componente inativo: produto fora do contrato do Live Product Options:', this.#dados.handle);
        this.hidden = true;
        return;
      }

      const validacao = window.FoxTheme.bordadoGeometria.validarCalibragem(this.#dados.calibragem);

      // Com calibragem recusada, o componente some e o bloco do Live Product
      // Options continua sendo o que a cliente vê.
      if (!validacao.ok) {
        console.warn('[bordado] componente inativo:', validacao.motivo ?? 'calibragem inválida');
        this.hidden = true;
        return;
      }

      this.#montar();
      this.#suprimirUiDoApp();
      this.#interceptarEnvio();
      this.#pronto = true;
    }

    /** Há personalização confirmada esperando o envio? */
    get temPersonalizacao() {
      return this.#personalizacao !== null;
    }

    /**
     * Este produto aceita bordado nesta página? Só vale depois de o componente se
     * conectar: sem contrato do app, sem cobrança ou com calibragem recusada ele se
     * esconde e isto devolve `false`. A oferta por quantidade (ADR-0028) consulta
     * antes de mostrar o painel "Seu kit" com os botões de bordado.
     */
    get bordavel() {
      return this.#pronto;
    }

    /** Produtos de cobrança lidos pelo modal: `{texto, logo, icone}`, cada um `{varianteId, preco}` ou `null`. */
    get cobrancas() {
      return this.#cobrancas;
    }

    /**
     * Mostra ou esconde a linha "Personalize". Com o cartão de 3 unidades da oferta
     * por quantidade o bordado passa a ser só pelo painel "Seu kit" (ADR-0028), e a
     * linha sai da página para não haver dois caminhos.
     * @param {boolean} visivel
     */
    definirLinhaVisivel(visivel) {
      // Pela referência guardada: depois de `#reposicionarBotao` a linha mora no
      // formulário de compra, fora deste componente, e `$` não a acha mais.
      const linha = this.#linha ?? this.$('personalize');
      if (linha) linha.hidden = !visivel;
    }

    /**
     * Properties de personalização de um bordado confirmado no modal, no vocabulário
     * do Live Product Options. É `montar` com o contrato desta página.
     * @param {{campos: Record<string,string>, cor: string, fonte: string, logo: boolean, lado: string|null, icone?: string|null}} bordado
     * @returns {Record<string, string>}
     */
    propriedadesDoBordado(bordado) {
      return window.FoxTheme.bordadoPropriedades.montar(bordado, this.#contrato);
    }

    disconnectedCallback() {
      if (this.#aoEnviar) document.removeEventListener('submit', this.#aoEnviar, true);
      this.#aoEnviar = null;
    }

    /**
     * Assume o envio do formulário de compra quando há personalização.
     *
     * Escuta o `document` na fase de captura para rodar antes do listener do
     * próprio formulário do tema. Sem personalização confirmada, não faz nada e
     * o tema envia a peça normalmente.
     */
    #interceptarEnvio() {
      this.#aoEnviar = (evento) => {
        if (!this.#personalizacao || evento.target !== this.#formularioDeCompra()) return;

        evento.preventDefault();
        evento.stopImmediatePropagation();
        this.#enviarComBordado(evento.target, evento.submitter);
      };

      document.addEventListener('submit', this.#aoEnviar, true);
    }

    /**
     * Tira da PDP a personalização que o Live Product Options desenha sozinho.
     *
     * Pular o app block no Liquid não basta: o *app embed* — que precisa
     * continuar carregado, porque é ele que publica o contrato — procura o
     * container dos botões de compra e injeta a UI própria por JavaScript.
     * Sem isto a cliente vê duas ofertas de bordado, e as duas gravam a mesma
     * line item property no mesmo formulário.
     *
     * Esconder não basta: campo escondido continua sendo enviado. Os controles
     * são desabilitados, que é o que faz o navegador não serializá-los.
     *
     * O app injeta depois de nós e redesenha quando a variante muda, então a
     * varredura fica num observador em vez de rodar uma vez.
     */
    #suprimirUiDoApp() {
      const alvo = this.#formularioDeCompra();
      if (!alvo) return;

      const varrer = () => {
        alvo.querySelectorAll('.cl-po--option:not([data-bordado-suprimido])').forEach((opcao) => {
          const controles = [...opcao.querySelectorAll('input, select, textarea')];
          const nomes = controles.map((c) => c.getAttribute('name'));

          const nome = opcao.dataset.option;
          const contrato = window.FoxTheme.bordadoContrato;
          const suprimir =
            contrato.ehOpcaoDeBordado(nome, nomes, this.#contrato) ||
            contrato.ehVarianteDuplicada(nomes, this.#temSeletorProprio(nome));

          if (!suprimir) return;

          opcao.dataset.bordadoSuprimido = '';
          opcao.style.setProperty('display', 'none', 'important');
          controles.forEach((controle) => {
            controle.disabled = true;
          });
        });
      };

      varrer();
      new MutationObserver(varrer).observe(alvo, { childList: true, subtree: true });
    }

    /**
     * O tema já oferece esta opção de variante em algum lugar da página?
     *
     * A comparação é pelo atributo `name`, que no seletor do tema é o nome da
     * opção — "Tamanho". Feita por varredura, e não por seletor montado com o
     * nome, para não depender de escape de um texto que vem do app.
     */
    #temSeletorProprio(nome) {
      if (!nome) return false;

      return [...document.querySelectorAll('input[name], select[name]')].some(
        (controle) => controle.getAttribute('name') === nome && !controle.closest('.cl-po--wrapper')
      );
    }

    /** Elemento por papel, como no resto do tema. */
    $(papel) {
      return this.querySelector(`[data-papel="${papel}"]`);
    }

    /** Troca `{{ nome }}` pelos valores, como o `interpolar` da grade. */
    #interpolar(texto, valores) {
      return (texto ?? '').replace(/{{\s*(\w+)\s*}}/g, (_, nome) => valores[nome] ?? '');
    }

    /**
     * Rotula a linha "Personalize" com o MENOR preço entre os produtos de
     * cobrança disponíveis (texto, logo, ícone): é o que a cliente paga para
     * começar. Os preços saem dos produtos, nunca do option set do app.
     */
    #rotularBotao() {
      const t = this.#dados.textos;
      const menor = window.FoxTheme.bordadoModalEstado.menorPreco(
        ['texto', 'logo', 'icone'].map((tipo) => this.#cobrancas[tipo]?.preco)
      );
      const valor = window.FoxTheme.Currency.formatMoney(menor, window.FoxTheme.settings.moneyFormat);
      const aPartirDe = this.#interpolar(t.a_partir_de, { valor });

      this.$('personalize-titulo').textContent = t.personalize;
      this.$('abrir-texto').textContent = t.abrir;
      this.$('abrir-preco').textContent = aPartirDe;
      this.#botao.setAttribute('aria-label', this.#interpolar(t.abrir_aria, { valor }));
    }

    /**
     * Leva a linha (título "Personalize" + botão) para junto do "Adicionar ao
     * carrinho", que é onde o app desenhava e onde a cliente procura.
     *
     * Feito em JavaScript, e não movendo o bloco nos 74 templates, porque a
     * posição que importa é dentro do formulário de compra — não é uma posição
     * que o `block_order` saiba expressar. Só a linha viaja: o `<dialog>` fica
     * no componente, para não aninhar um formulário dentro do outro.
     */
    #reposicionarBotao() {
      const submit = this.#formularioDeCompra()?.querySelector('.product-form__submit');
      if (!submit || !submit.parentElement) return;

      submit.parentElement.insertBefore(this.#linha, submit);
    }

    #montar() {
      this.#linha = this.$('personalize');
      this.#botao = this.$('abrir');
      this.#rotularBotao();
      this.#botao.addEventListener('click', () => this.#abrir());
      this.#reposicionarBotao();
    }

    /**
     * URL da foto no tamanho que o modal usa. O original da galeria tem de 600
     * a 900 KB; pedir dimensionado ao CDN é o que mantém a abertura rápida.
     */
    #fotoDimensionada(largura) {
      return `${this.#dados.foto.split('?')[0]}?width=${largura}`;
    }

    /** As fontes do modal que são da página: foto e calibragem, tamanho, cor e ícones. */
    #fontesDoModal() {
      return {
        foto: this.#fotoDimensionada(LARGURA_CANVAS),
        calibragem: this.#dados.calibragem,
        tamanho: this.#fonteTamanho(),
        cor: this.#fonteCor(),
        icones: this.#icones(),
      };
    }

    /** Abre o modal com as fontes da PDP: variante da página, cor do produto, envio ao carrinho. */
    #abrir() {
      const precoPeca = () => this.#varianteAtual()?.price ?? this.#dados.precoPeca;

      this.#modal.abrir({
        produto: { preco: precoPeca },
        ...this.#fontesDoModal(),
        rodape: { rotulo: 'confirmar', exigeTamanho: Boolean(this.#dados.tamanho), exigeBordado: false },
        aoConfirmar: (resultado) => this.#aoConfirmar(resultado),
      });
    }

    /**
     * Abre o modal para o bordado de UMA peça do painel "Seu kit" da oferta por
     * quantidade (ADR-0028), com as mesmas fontes da página — cor e tamanho do seletor
     * da PDP, foto do produto — e o rodapé "Confirmar bordado" da grade de kit. Quem
     * chama recebe o bordado em `aoConfirmar` e decide o que fazer com ele: este
     * componente não envia nada ao carrinho por aqui.
     *
     * Como o `foto` vem de `#fontesDoModal()` a cada abertura, trocar de variante ou de
     * produto entre uma abertura e outra já usa a foto certa.
     *
     * @param {{inicial?: object|null, aoConfirmar: Function, aoFechar?: Function}} args
     *   `inicial` é o `estado` que o modal devolveu antes (editar o bordado da peça).
     * @returns {boolean} `false` se o produto não aceita bordado ou o contrato do app não está disponível.
     */
    abrirParaPeca({ inicial = null, aoConfirmar, aoFechar }) {
      if (!this.#pronto) return false;

      return this.#modal.abrir({
        produto: null,
        ...this.#fontesDoModal(),
        inicial,
        rodape: { rotulo: 'confirmar_bordado', exigeTamanho: Boolean(this.#dados.tamanho), exigeBordado: true },
        aoConfirmar,
        aoFechar,
      });
    }

    /**
     * Lista fixa de ícones e foto de posição (`snippets/bordado-icones.liquid`),
     * ou `null` quando a página não a entrega — aí o modal não oferece ícone.
     */
    #icones() {
      try {
        const lista = JSON.parse(this.querySelector('[data-bordado-icones]')?.textContent ?? 'null');

        // `bordadoIcone` (ENN-95): calibragem e foto do ícone, ao lado da lista; o modal
        // confere os campos e, faltando algo, cai na foto de posição.
        return lista?.lista?.length ? { ...lista, bordadoIcone: this.#dados.bordadoIcone ?? null } : null;
      } catch {
        return null;
      }
    }

    // ---------------------------------------------------------------- fontes do modal

    /** Variantes da página, do JSON do próprio seletor de variante do tema. */
    #variantes() {
      const escopo = this.closest('.shopify-section') ?? document;
      const json = escopo.querySelector('variant-selects script[type="application/json"]');

      try {
        return JSON.parse(json?.textContent ?? '[]');
      } catch {
        return [];
      }
    }

    /** Variante escolhida na página: a do `input[name="id"]` do formulário de compra. */
    #varianteAtual() {
      const id = Number(this.#formularioDeCompra()?.querySelector('input[name="id"]')?.value);

      return this.#variantes().find((variante) => variante.id === id) ?? null;
    }

    /**
     * Fonte de tamanho do modal: o seletor de variante da página, nos dois
     * sentidos. Escolher no modal marca o controle da página e dispara o
     * `change` que o `variant-selects` já escuta; mexer na página dispara
     * `variant:changed`, que o modal assina para se redesenhar.
     *
     * Esgotado = nenhuma variante disponível com aquele tamanho e as demais
     * opções iguais às da variante atual.
     *
     * @returns {{ler: Function, escolher: Function, assinar: Function}|null}
     */
    #fonteTamanho() {
      const opcao = this.#dados.tamanho;
      if (!opcao) return null;

      const indice = opcao.posicao - 1;

      return {
        ler: () => {
          const variantes = this.#variantes();
          const atual = this.#varianteAtual();
          const valores = [...new Set(variantes.map((v) => v.options?.[indice]).filter(Boolean))];

          const mesmasOutrasOpcoes = (variante) =>
            !atual || (variante.options ?? []).every((valor, i) => i === indice || valor === atual.options?.[i]);

          return {
            opcoes: valores.map((valor) => ({
              valor,
              disponivel: variantes.some(
                (v) => v.available && v.options?.[indice] === valor && mesmasOutrasOpcoes(v)
              ),
            })),
            atual: atual?.options?.[indice] ?? null,
          };
        },
        escolher: (valor) => {
          const escopo = this.closest('.shopify-section') ?? document;
          const controles = [...escopo.querySelectorAll('variant-selects input, variant-selects select')].filter(
            (controle) => controle.getAttribute('name') === opcao.nome
          );

          controles.forEach((controle) => {
            if (controle.tagName === 'SELECT') {
              if (controle.value === valor) return;
              controle.value = valor;
            } else if (controle.value === valor && !controle.checked) {
              controle.checked = true;
            } else {
              return;
            }

            controle.dispatchEvent(new Event('change', { bubbles: true }));
          });
        },
        assinar: (aoMudar) => {
          document.addEventListener('variant:changed', aoMudar);

          return () => document.removeEventListener('variant:changed', aoMudar);
        },
      };
    }

    /**
     * Fonte de cor do modal.
     *
     * Com a flag `bordado_modal_cor` desligada (o padrão) o modal não mostra a
     * linha Cor (ENN-94). Ligada, ele lista `irmas()` e trocar a cor leva à página do
     * produto daquela cor — foto, preço e calibragem vêm com ela.
     */
    #fonteCor() {
      const cor = this.#dados.cor;
      if (!cor?.nome) return null;

      return {
        atual: cor,
        irmas: () => this.#coresIrmas(),
        escolher: (irma) => {
          if (irma.url && irma.nome !== cor.nome) window.location.href = irma.url;
        },
      };
    }

    /**
     * PONTO DE PLUG das cores do mesmo modelo.
     *
     * Hoje cada cor é um produto separado e o tema não sabe quais são irmãs: o
     * metafield `custom.modelo` está vazio na loja (ADR-0016). Por isso devolve só
     * a cor atual. Quando o cadastro existir, é aqui que a lista entra: um item
     * `{ nome, hex, url }` por cor irmã (o `url` é o que `escolher` abre), lido do
     * metaobjeto `modelo`, que o Liquid pode entregar em `#dados` como já faz
     * `snippets/product-color-siblings.liquid`. Nada mais no modal muda.
     *
     * @returns {{nome: string, hex?: string, url?: string}[]}
     */
    #coresIrmas() {
      return [this.#dados.cor];
    }

    // ---------------------------------------------------------------- envio

    /**
     * Rodapé do modal na PDP. Sem bordado, adiciona a peça simples pelo fluxo
     * normal do tema; com bordado, deixa a personalização esperando o envio que
     * o `#aoEnviar` intercepta (peça + cobranças, ADR-0023).
     *
     * A personalização vale só para este envio: um clique depois no "Adicionar ao
     * carrinho" da página adiciona a peça sem bordado.
     *
     * @param {{dados: object|null, arquivoLogo: File|null, estado: object}} resultado
     * @returns {string|undefined} texto de erro; o modal fica aberto
     */
    #aoConfirmar({ dados, arquivoLogo, estado }) {
      const form = this.#formularioDeCompra();

      if (!form) {
        console.error('[bordado] formulário de compra não encontrado; a personalização não seria gravada');
        return this.#dados.textos.indisponivel;
      }

      // Resto de versões antigas do componente, que injetavam hidden inputs.
      form.querySelectorAll('[data-bordado-prop]').forEach((velho) => velho.remove());

      // Variante esgotada ou envio em andamento: o tema recusaria em silêncio,
      // com o modal já fechado. Melhor avisar aqui, com o texto preenchido.
      const botaoDeCompra = form.submitButtonElement ?? form.querySelector('.product-form__submit');
      if (botaoDeCompra?.disabled || botaoDeCompra?.hasAttribute('aria-disabled')) {
        return this.#dados.textos.indisponivel;
      }

      this.#personalizacao = dados ? window.FoxTheme.bordadoPropriedades.montar(dados, this.#contrato) : null;
      this.#arquivoLogo = dados ? arquivoLogo : null;
      // Sem texto (só ícone e/ou logo): não entra a linha `Bordado` (ADR-0027, D30).
      this.#cobrarTexto = Boolean(estado?.texto);

      // Depois que o modal fechar: o envio abre a gaveta do carrinho por cima.
      setTimeout(() => form.requestSubmit(), 0);
    }

    /**
     * Formulário de compra desta PDP.
     *
     * Ancorado em `data-type="add-to-cart-form"`, que é como o tema marca o
     * formulário que realmente adiciona ao carrinho. Buscar por `action` pegava
     * o formulário de parcelamento do bloco `price`, que vem antes na ordem da
     * página e nunca é submetido — as properties sumiam sem erro.
     *
     * @returns {HTMLFormElement|null}
     */
    #formularioDeCompra() {
      const escopo = this.closest('.shopify-section') ?? document;

      return escopo.querySelector('form[data-type="add-to-cart-form"]');
    }


    /**
     * Envia peça + bordado ao carrinho numa requisição só.
     *
     * Uma requisição, e não duas, para a guarda do carrinho (`kit-carrinho.js`)
     * nunca ver o par pela metade: ela reconcilia a cada `/cart/add` e
     * apagaria a personalização de uma peça cuja cobrança ainda não chegou.
     *
     * O tema continua fazendo o envio — mesma rotina `handleFormSubmission`,
     * mesma gaveta, mesmos eventos —, só que com o corpo em JSON com as duas
     * linhas. Replica o que o `handleFormSubmit` de `assets/theme.js` faz antes
     * de chamá-la.
     *
     * @param {HTMLFormElement} form
     * @param {HTMLElement|null} [origem] botão que disparou o envio
     */
    #enviarComBordado(form, origem) {
      // Este é o fluxo da linha "Personalize", que só existe com o cartão de 1
      // unidade. Com o cartão de 3 da oferta por quantidade quem monta e envia é a
      // própria oferta, com o bordado de cada peça (ADR-0028).
      const items = window.FoxTheme.bordadoPropriedades.montarItens({
        entradas: new FormData(form),
        personalizacao: this.#personalizacao,
        cobranca: this.#cobrarTexto ? this.#cobrancas.texto : null,
        cobrancaLogo: this.#cobrancas.logo,
        cobrancaIcone: this.#cobrancas.icone,
        // `<grupo>#<posição>`: a Function de desconto (ADR-0014) agrupa pelo
        // trecho antes do `#`. `pdp-…` nunca tem regra configurada, então o
        // bordado da PDP não recebe desconto de kit.
        referencia: `pdp-${Date.now().toString(36)}#1`,
      });
      const arquivo = this.#arquivoLogo;
      this.#personalizacao = null;
      this.#arquivoLogo = null;

      if (arquivo) {
        this.enviarComArte(form, items, [{ item: items[0], arquivo }], origem);
        return;
      }

      const carrinhoEmPagina =
        window.FoxTheme.settings.cartType === 'page' || document.body.classList.contains('cart-template');

      if (typeof form.handleFormSubmission !== 'function' || carrinhoEmPagina) {
        this.#enviarParaPaginaDeCarrinho(items);
        return;
      }

      const botao = form.submitButtonElement;
      if (botao?.hasAttribute('aria-disabled')) return;

      form.lastSubmittedElement = origem || form;
      form.displayFormErrors?.();

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const config = window.FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname });

      botao?.setAttribute('aria-disabled', 'true');
      botao?.classList.add('btn--loading');

      form.handleFormSubmission(config);
    }

    /**
     * Envio com arte de logo: cada peça com arte vai sozinha em multipart e o resto
     * (as cobranças, e na oferta as peças sem arte) em JSON na chamada seguinte, como
     * na grade de kit. A oferta por quantidade (ADR-0028) também envia por aqui, com
     * até uma chamada multipart por peça com logo.
     *
     * Entre as chamadas o carrinho tem peças personalizadas sem a linha que as cobra,
     * estado que a guarda (`kit-carrinho.js`) leria como divergência. Os eventos
     * `kit:envio-iniciado`/`kit:envio-concluido` a fazem esperar e reconciliar uma
     * vez só, sobre o resultado inteiro; se algo falhar no meio, é ela quem limpa o
     * que ficou pela metade.
     *
     * A conferência (`kit-conferencia.js`, ENN-96) lê o carrinho antes e depois,
     * reenvia uma vez o que não entrou e, se ainda faltar, desfaz o envio e
     * devolve `false` com a mensagem de erro: a cliente vê tudo ou nada.
     *
     * @param {HTMLFormElement} form
     * @param {object[]} items todas as linhas: peças, cobranças e o que mais for junto
     * @param {{item: object, arquivo: File}[]} comArte as linhas de `items` que levam arte
     * @param {HTMLElement|null} origem
     * @param {'pdp'|'oferta'} [origemDoEnvio] de onde veio o envio, para a telemetria da conferência
     * @returns {Promise<boolean>} `true` se tudo entrou no carrinho
     */
    async enviarComArte(form, items, comArte, origem, origemDoEnvio = 'pdp') {
      const artes = new Map(comArte.map(({ item, arquivo }) => [item, arquivo]));
      const resto = items.filter((item) => !artes.has(item));
      const carrinhoEmPagina =
        window.FoxTheme.settings.cartType === 'page' || document.body.classList.contains('cart-template');
      const botao = form.submitButtonElement ?? form.querySelector('.product-form__submit');

      if (botao?.hasAttribute('aria-disabled')) return false;

      form.lastSubmittedElement = origem || form;
      form.displayFormErrors?.();
      botao?.setAttribute('aria-disabled', 'true');
      botao?.classList.add('btn--loading');
      document.dispatchEvent(new CustomEvent('kit:envio-iniciado'));

      try {
        const conferencia = window.FoxTheme.kitConferencia;
        const lerResposta = async (resposta) => {
          const corpo = await resposta.json();
          if (corpo.status) throw new Error(corpo.description || corpo.message);

          return corpo;
        };

        // Resposta sem erro não prova que a peça entrou (ENN-96): a conferência
        // lê o carrinho antes e depois, reenvia uma vez o que faltar e, se ainda
        // faltar, desfaz o envio e lança. Vale também para o carrinho em página,
        // que só redireciona depois de conferido.
        const { ultima: adicionada } = await conferencia.enviarConferindo({
          comArte: comArte.map(({ item, arquivo }) => ({ linha: item, arquivo })),
          linhas: resto,
          origem: origemDoEnvio,
          enviarComArte: async (peca, arquivo) =>
            // Sem `Content-Type` de propósito: o navegador escreve o boundary.
            lerResposta(
              await fetch(window.FoxTheme.routes.cart_add_url, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                body: window.FoxTheme.bordadoPropriedades.montarCorpoComArte(peca, arquivo),
              })
            ),
          enviarLinhas: async (lote) =>
            lerResposta(
              await fetch(window.FoxTheme.routes.cart_add_url, {
                ...window.FoxTheme.utils.fetchConfig('javascript'),
                body: JSON.stringify({ items: lote }),
              })
            ),
          lerCarrinho: () => conferencia.lerItensDoCarrinho(),
          reverter: (atualizacoes) => conferencia.reverterNoCarrinho(atualizacoes),
        });

        if (carrinhoEmPagina) {
          window.location.href = window.FoxTheme.routes.cart_url;
          return true;
        }

        // Carrinho já com o HTML das sections que assinam `cartUpdate`: a
        // gaveta lê `cart.sections[id]` sem guarda (mesma razão da grade).
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        const carrinho = await (
          await fetch(window.FoxTheme.routes.cart_update_url, {
            ...window.FoxTheme.utils.fetchConfig(),
            body: JSON.stringify({ updates: {}, sections, sections_url: window.location.pathname }),
          })
        ).json();

        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: carrinho });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: adicionada } }));
        form.showCartDrawer?.();

        return true;
      } catch (erro) {
        console.error('[bordado] não foi possível adicionar peça, bordado e logo ao carrinho', erro);
        form.displayFormErrors?.(
          erro.codigo === 'kit_envio_divergente' ? this.#dados.textos.envio_incompleto : erro.message
        );

        return false;
      } finally {
        botao?.classList.remove('btn--loading');
        botao?.removeAttribute('aria-disabled');
        document.dispatchEvent(new CustomEvent('kit:envio-concluido'));
      }
    }

    /** Carrinho em página: adiciona as duas linhas e leva a cliente até ele. */
    async #enviarParaPaginaDeCarrinho(items) {
      try {
        const resposta = await fetch(`${window.FoxTheme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!resposta.ok) throw new Error(`cart/add respondeu ${resposta.status}`);

        window.location.href = window.FoxTheme.routes.cart_url;
      } catch (erro) {
        console.error('[bordado] não foi possível adicionar peça e bordado ao carrinho', erro);
      }
    }
  }

  customElements.define('bordado-pdp', BordadoPdp);
})(window, customElements, HTMLElement);

